import { useEffect, useRef, useState } from 'react';
import io from 'socket.io-client';
import { CAAS_SOCKET_PATH, CAAS_SOCKET_URL } from '../common/utils/Constants';
import { refreshBrokerBearerToken } from '../util/brokerAuth';

export const DOCUMENT_PARSE_PROGRESS = 'DOCUMENT_PARSE_PROGRESS';
export const DOCUMENT_PARSE_CHANNEL = 'documentParse';

export const SOCKET_EVENT = {
    SUBSCRIBE: 'SUBSCRIBE',
    SUBSCRIBED: 'SUBSCRIBED',
    UNSUBSCRIBE: 'UNSUBSCRIBE',
    AUTH_REFRESH: 'AUTH_REFRESH',
    AUTH_EXPIRING: 'AUTH_EXPIRING',
    AUTH_EXPIRED: 'AUTH_EXPIRED',
};

const TOKEN_KEY = 'BearerToken';

const readToken = () => {
    try {
        return localStorage.getItem(TOKEN_KEY) || '';
    } catch (e) {
        return '';
    }
};

const useDocumentParseProgress = (formId, { enabled = true, onTerminal, uploadEpoch = 0 } = {}) => {
    const [state, setState] = useState(null);
    const [progress, setProgress] = useState(null);
    const [error, setError] = useState(null);
    const [transportError, setTransportError] = useState(null);
    const [connected, setConnected] = useState(false);
    const onTerminalRef = useRef(onTerminal);
    const notifiedTerminalRef = useRef(null);

    useEffect(() => {
        onTerminalRef.current = onTerminal;
    }, [onTerminal]);

    useEffect(() => {
        if (!uploadEpoch) return;
        setState(null);
        setProgress(null);
        setError(null);
        notifiedTerminalRef.current = null;
    }, [uploadEpoch]);

    useEffect(() => {
        setState(null);
        setProgress(null);
        setError(null);
        setTransportError(null);

        if (!enabled || !formId) return undefined;

        let cancelled = false;

        let socket;
        try {
            socket = io(CAAS_SOCKET_URL || undefined, {
                path: CAAS_SOCKET_PATH,
                transports: ['websocket'],
                auth: (cb) => cb({ token: readToken() }),
            });
        } catch (e) {
            return undefined;
        }

        const notifiedTerminal = notifiedTerminalRef;

        const applyEvent = (payload) => {
            if (!payload || (payload.formId && payload.formId !== formId)) return;
            const terminal = payload.state === 'completed' || payload.state === 'failed';
            if (payload.state && !terminal && notifiedTerminal.current) {
                setProgress(null);
                setError(null);
            }
            if (payload.state) setState(payload.state);
            if (payload.progress) setProgress(payload.progress);
            if (payload.error) setError(payload.error);
            if (!terminal) {
                if (payload.state) notifiedTerminal.current = null;
                return;
            }
            if (notifiedTerminal.current === payload.state) return;
            notifiedTerminal.current = payload.state;
            onTerminalRef.current?.(payload);
        };

        const handleSubscribeAck = (ack) => {
            if (cancelled) return;
            if (!ack || ack.ok !== true) {
                setConnected(false);
                setTransportError(ack?.reason || 'subscribe_failed');
                return;
            }
            setConnected(true);
            setTransportError(null);
            if (ack.snapshot) applyEvent(ack.snapshot);
        };

        const handleConnect = () => {
            socket.emit(
                SOCKET_EVENT.SUBSCRIBE,
                { channel: DOCUMENT_PARSE_CHANNEL, id: formId },
                handleSubscribeAck,
            );
        };

        const handleDisconnect = () => setConnected(false);

        const handleConnectError = (err) => {
            setConnected(false);
            const reason = err?.data?.reason || err?.message;
            if (reason) setTransportError(reason);
        };

        const handleAuthExpiring = async () => {
            const inUse = readToken();
            try {
                await refreshBrokerBearerToken(inUse);
            } catch (e) {
            }
            if (cancelled) return;
            const fresh = readToken();
            if (!fresh) {
                setConnected(false);
                return;
            }
            socket.emit(SOCKET_EVENT.AUTH_REFRESH, { token: fresh }, (ack) => {
                if (cancelled) return;
                if (!ack || ack.ok !== true) {
                    setConnected(false);
                    setTransportError(ack?.reason || 'auth_refresh_failed');
                }
            });
        };

        const handleAuthExpired = () => {
            setConnected(false);
        };

        socket.on('connect', handleConnect);
        socket.on('disconnect', handleDisconnect);
        socket.on('connect_error', handleConnectError);
        socket.on(SOCKET_EVENT.AUTH_EXPIRING, handleAuthExpiring);
        socket.on(SOCKET_EVENT.AUTH_EXPIRED, handleAuthExpired);
        socket.on(DOCUMENT_PARSE_PROGRESS, applyEvent);

        return () => {
            cancelled = true;
            socket.off('connect', handleConnect);
            socket.off('disconnect', handleDisconnect);
            socket.off('connect_error', handleConnectError);
            socket.off(SOCKET_EVENT.AUTH_EXPIRING, handleAuthExpiring);
            socket.off(SOCKET_EVENT.AUTH_EXPIRED, handleAuthExpired);
            socket.off(DOCUMENT_PARSE_PROGRESS, applyEvent);
            socket.disconnect();
        };
    }, [formId, enabled]);

    return {
        state,
        progress,
        error,
        transportError,
        connected,
        realtimeAvailable: true,
    };
};

export default useDocumentParseProgress;

